import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import { UnitCard } from "@/components/catalog/UnitCard";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABELS, CATEGORY_SLUGS_TO_KEY } from "@/lib/catalog";

const ESTADOS = ["Disponible", "Apartado", "Próximamente"];

export default function CatalogGrid() {
  const { category: slug } = useParams();
  const category = CATEGORY_SLUGS_TO_KEY[slug];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("all");

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    api
      .get(`/catalog/units?category=${category}`)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (estado !== "all" && it.estado !== estado) return false;
      if (!q.trim()) return true;
      const t = q.toLowerCase();
      return [it.titulo, it.marca, it.modelo, it.version, String(it.anio)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(t));
    });
  }, [items, q, estado]);

  if (!category) return <Navigate to="/" replace />;
  const label = CATEGORY_LABELS[category] || category;

  return (
    <div className="min-h-screen bg-white text-[#3E5641]" data-testid={`page-catalog-${category}`}>
      <PublicHeader />
      <main className="container-x pt-32 pb-24 md:pt-40">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Inventario</p>
            <h1 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">{label}</h1>
            <p className="mt-2 text-sm text-[#6B7678]" data-testid="catalog-count">
              {loading ? "Cargando…" : `${filtered.length} ${filtered.length === 1 ? "unidad" : "unidades"}`}
            </p>
          </div>
          <div className="flex gap-3">
            <Input
              placeholder="Buscar marca, modelo, año…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-64"
              data-testid="catalog-search"
            />
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="w-44" data-testid="catalog-filter-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-[#6B7678]" data-testid="catalog-loading">Cargando…</p>
        ) : filtered.length === 0 ? (
          <div className="rounded-sm border border-[#3E5641]/10 bg-[#F1F3F3] px-6 py-16 text-center" data-testid="catalog-empty">
            <p className="font-serif text-2xl text-[#3E5641]">Sin unidades por ahora.</p>
            <p className="mt-2 text-sm text-[#6B7678]">
              Estamos preparando el inventario de {label.toLowerCase()}. Mientras tanto, puedes iniciar tu crédito.
            </p>
            <Link to="/comprar" className="btn-primary mt-6 inline-flex">Empezar mi crédito</Link>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
            {filtered.map((u) => <UnitCard key={u.id} unit={u} />)}
          </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
